/**
 * MedicalProviderEntityDetails - Detailed view for a Medical Provider entity
 *
 * Displays all fields from MedicalProviderEntitySchema with:
 * - Change highlighting for affected fields
 * - Support for editable mode
 * - Proper formatting for dates, enums, contact handles
 * - Contact Information section with all handles
 * - Record Requests section
 */

'use client';

import {
  Building2,
  Calendar,
  FileText,
  Mail,
  MapPin,
  Phone,
  Printer,
} from 'lucide-react';
import React, { useMemo } from 'react';

import { ProviderServiceLink } from '@/components/entity-links';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type {
  MedicalProviderEntitySchema,
  RecordRequestSchema,
} from '@/services/api/generated/entitySyncService';

import {
  FieldRow,
  formatEnumValue,
  getSuggestedValue,
  isFieldAffected,
} from './EntityFieldComponents';
import {
  MEDICAL_PROVIDER_FIELD_CONFIGS,
  RECORD_REQUEST_STATUS_LABELS,
  toFieldDefinitions,
} from './fieldConfigs';
import type { FieldDefinition } from './types';

// ============================================================================
// Types
// ============================================================================

export interface MedicalProviderEntityDetailsProps {
  /** The medical provider entity to display. Optional for CREATE scenarios. */
  entity?: MedicalProviderEntitySchema | null;
  /** Set of field names that have suggested changes */
  affectedFields?: Set<string>;
  /** Payload containing field updates to apply */
  suggestedPayload?: MedicalProviderEntitySchema;
  /** Whether fields should be editable */
  editable?: boolean;
  /** Callback when a field value changes */
  onPayloadChange?: (
    fieldName: keyof MedicalProviderEntitySchema,
    value: string | null,
  ) => void;
}

// ============================================================================
// Constants
// ============================================================================

// ============================================================================
// Helper Functions
// ============================================================================

/** Extract all handles of a specific type */
function getHandlesByType(
  entity: MedicalProviderEntitySchema | null | undefined,
  handleType: 'PHONE' | 'EMAIL' | 'FAX',
): string[] {
  if (!entity?.handles) return [];
  return entity.handles
    .filter((h) => h.handle_type === handleType)
    .map((h) => ('handle_value' in h ? h.handle_value : ''))
    .filter(Boolean);
}

/** Extract all addresses from handles */
function getAddresses(
  entity: MedicalProviderEntitySchema | null | undefined,
): { formatted: string; label: string | null | undefined }[] {
  if (!entity?.handles) return [];
  const addressHandles = entity.handles.filter(
    (
      h,
    ): h is Extract<
      (typeof entity.handles)[number],
      { handle_type: 'ADDRESS' }
    > => h.handle_type === 'ADDRESS',
  );
  return addressHandles.map((h) => {
    const parts = [
      h.line1,
      h.line2,
      h.line3,
      [h.city, h.state, h.zip].filter(Boolean).join(', '),
      h.country !== 'US' ? h.country : null,
    ].filter(Boolean);
    return {
      formatted: parts.join(', '),
      label: h.label,
    };
  });
}

/** Format a date string for display */
function formatDate(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/** Core field definitions for medical provider, derived from centralized config */
const CORE_FIELD_DEFINITIONS: FieldDefinition[] = toFieldDefinitions(
  MEDICAL_PROVIDER_FIELD_CONFIGS,
  {
    provider_service_id: {
      renderCustomValue: (value) => (
        <ProviderServiceLink entityId={value} className="text-sm">
          Provider Directory
        </ProviderServiceLink>
      ),
    },
  },
);

// ============================================================================
// Sub-components
// ============================================================================

/** Contact information section */
const ContactInfoSection: React.FC<{
  entity: MedicalProviderEntitySchema | null | undefined;
}> = ({ entity }) => {
  if (!entity?.handles || entity.handles.length === 0) {
    return null;
  }

  const phones = getHandlesByType(entity, 'PHONE');
  const emails = getHandlesByType(entity, 'EMAIL');
  const faxes = getHandlesByType(entity, 'FAX');
  const addresses = getAddresses(entity);

  const hasAnyContact =
    phones.length > 0 ||
    emails.length > 0 ||
    faxes.length > 0 ||
    addresses.length > 0;

  if (!hasAnyContact) {
    return null;
  }

  return (
    <Card className="border-zinc-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium text-zinc-700">
          Contact Information
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Phone numbers */}
        {phones.length > 0 && (
          <div className="space-y-1">
            {phones.map((phone, idx) => (
              <div
                key={`phone-${idx}`}
                className="flex items-center gap-2 text-sm"
              >
                <Phone className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                <span className="text-zinc-900">{phone}</span>
              </div>
            ))}
          </div>
        )}

        {/* Emails */}
        {emails.length > 0 && (
          <div className="space-y-1">
            {emails.map((email, idx) => (
              <div
                key={`email-${idx}`}
                className="flex items-center gap-2 text-sm"
              >
                <Mail className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                <span className="text-zinc-900">{email}</span>
              </div>
            ))}
          </div>
        )}

        {/* Fax numbers */}
        {faxes.length > 0 && (
          <div className="space-y-1">
            {faxes.map((fax, idx) => (
              <div
                key={`fax-${idx}`}
                className="flex items-center gap-2 text-sm"
              >
                <Printer className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                <span className="text-zinc-900">{fax}</span>
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  Fax
                </Badge>
              </div>
            ))}
          </div>
        )}

        {/* Addresses */}
        {addresses.length > 0 && (
          <div className="space-y-2">
            {addresses.map((addr, idx) => (
              <div
                key={`addr-${idx}`}
                className="flex items-start gap-2 text-sm"
              >
                <MapPin className="h-4 w-4 text-zinc-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-zinc-900">{addr.formatted}</span>
                  {addr.label && (
                    <Badge
                      variant="outline"
                      className="ml-2 text-[10px] px-1.5 py-0"
                    >
                      {addr.label}
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/** Record requests section */
const RecordRequestsSection: React.FC<{
  recordRequests: RecordRequestSchema[] | undefined;
}> = ({ recordRequests }) => {
  if (!recordRequests || recordRequests.length === 0) {
    return null;
  }

  return (
    <Card className="border-zinc-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-zinc-700">
            Record Requests
          </CardTitle>
          <Badge variant="secondary" className="text-xs">
            {recordRequests.length} request
            {recordRequests.length !== 1 ? 's' : ''}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {recordRequests.map((request, idx) => (
            <div
              key={request.id ?? idx}
              className="rounded-lg border border-zinc-100 bg-zinc-50 p-3"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-zinc-400" />
                  <span className="text-sm font-medium text-zinc-700">
                    Request for {request.provided_records?.join(', ')}
                  </span>
                </div>
                {request.request_status && (
                  <Badge
                    variant="outline"
                    className={cn(
                      'text-xs',
                      request.request_status === 'records_delivered' ||
                        request.request_status === 'records_ingested'
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                        : request.request_status === 'rejected'
                          ? 'border-rose-300 bg-rose-50 text-rose-700'
                          : 'border-amber-300 bg-amber-50 text-amber-700',
                    )}
                  >
                    {RECORD_REQUEST_STATUS_LABELS[request.request_status] ??
                      formatEnumValue(request.request_status)}
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                {request.request_date && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="text-zinc-500">Requested:</span>
                    <span className="text-zinc-900">
                      {formatDate(request.request_date)}
                    </span>
                  </div>
                )}
                {request.received_date && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 text-zinc-400" />
                    <span className="text-zinc-500">Received:</span>
                    <span className="text-zinc-900">
                      {formatDate(request.received_date)}
                    </span>
                  </div>
                )}
              </div>

              {request.notes && (
                <p className="mt-2 text-sm text-zinc-600 border-t border-zinc-200 pt-2">
                  {request.notes}
                </p>
              )}

              {request.files && request.files.length > 0 && (
                <div className="mt-2 flex items-center gap-1 text-xs text-zinc-500">
                  <FileText className="h-3 w-3" />
                  <span>
                    {request.files.length} file
                    {request.files.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const MedicalProviderEntityDetails: React.FC<
  MedicalProviderEntityDetailsProps
> = ({
  entity,
  affectedFields,
  suggestedPayload,
  editable,
  onPayloadChange,
}) => {
  const isCreate = !entity;
  const affectedSet = affectedFields ?? new Set<string>();
  const payload = suggestedPayload as Record<string, unknown> | undefined;

  // Group fields into rows of 2 for grid layout
  const fieldRows = useMemo(() => {
    const rows: FieldDefinition[][] = [];
    for (let i = 0; i < CORE_FIELD_DEFINITIONS.length; i += 2) {
      rows.push(CORE_FIELD_DEFINITIONS.slice(i, i + 2));
    }
    return rows;
  }, []);

  return (
    <div className="space-y-4">
      {/* Provider header card */}
      {(entity || suggestedPayload) && (
        <div className="flex items-start gap-3 p-4 rounded-lg border border-zinc-200 bg-white">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-blue-100">
            <Building2 className="h-5 w-5 text-blue-600" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="truncate text-lg font-semibold text-zinc-900">
              {entity?.name ?? suggestedPayload?.name ?? 'New Provider'}
            </h4>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {(entity?.status ?? suggestedPayload?.status) && (
                <Badge variant="secondary">
                  {formatEnumValue(
                    entity?.status ?? suggestedPayload?.status ?? null,
                  )}
                </Badge>
              )}
              {(entity?.care_level ?? suggestedPayload?.care_level) && (
                <Badge variant="outline" className="text-xs">
                  {formatEnumValue(
                    entity?.care_level ?? suggestedPayload?.care_level ?? null,
                  )}
                </Badge>
              )}
              {entity?.specialty && (
                <Badge variant="outline" className="text-xs bg-zinc-50">
                  {entity.specialty}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Core field details grid */}
      <div className="space-y-3">
        <p className="text-xs font-medium uppercase tracking-wide text-zinc-400">
          Provider Details
        </p>
        <div className={editable ? 'divide-y divide-zinc-100' : 'space-y-2'}>
          {fieldRows.map((row, rowIndex) => (
            <div
              key={rowIndex}
              className={cn(
                'grid gap-4',
                row.length === 2 ? 'grid-cols-2' : 'grid-cols-1',
                editable && 'py-2 first:pt-0 last:pb-0',
              )}
            >
              {row.map((fieldDef) => {
                const currentValue = fieldDef.getValue(entity);
                const suggested = getSuggestedValue(payload, fieldDef.key);
                const hasChange = isFieldAffected(affectedSet, fieldDef.key);

                return (
                  <FieldRow
                    key={fieldDef.key}
                    fieldDef={fieldDef}
                    currentValue={currentValue}
                    suggestedValue={suggested}
                    hasChange={hasChange}
                    isCreate={isCreate}
                    editable={editable ?? false}
                    onValueChange={
                      onPayloadChange && fieldDef.isEditable
                        ? (value) =>
                            onPayloadChange(
                              fieldDef.key as keyof MedicalProviderEntitySchema,
                              value,
                            )
                        : undefined
                    }
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Information Section */}
      <ContactInfoSection entity={entity} />

      {/* Record Requests Section */}
      <RecordRequestsSection recordRequests={entity?.record_requests} />
    </div>
  );
};

export default MedicalProviderEntityDetails;
